import { useMutation } from '@tanstack/react-query';
import { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import type { AppointmentDetail } from '@barbeiropro/contracts';
import { Field } from '@/components/shared/field';
import { FormModal } from '@/components/shared/form-modal';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { api, errorMessage, HttpError } from '@/lib/api';
import { useBarbers, useTimezone } from '@/lib/org';
import { useSession } from '@/lib/session';
import { emailNote, useAvailability, useInvalidateAgenda } from './api';
import { SlotPicker, type PickedSlot } from './slot-picker';
import { ymdOf } from './time';

export function RescheduleModal({ appt, open, onOpenChange }: { appt: AppointmentDetail; open: boolean; onOpenChange: (v: boolean) => void }) {
  const tz = useTimezone();
  const { can } = useSession();
  const barbers = useBarbers();
  const invalidate = useInvalidateAgenda();
  const [date, setDate] = useState(ymdOf(appt.startsAt, tz));
  const [barberId, setBarberId] = useState(appt.barberId);
  const [picked, setPicked] = useState<PickedSlot | null>(null);
  const [reason, setReason] = useState('');

  useEffect(() => {
    if (!open) return;
    setDate(ymdOf(appt.startsAt, tz));
    setBarberId(appt.barberId);
    setPicked(null);
    setReason('');
  }, [open, appt.id, appt.startsAt, appt.barberId, tz]);

  const options = useMemo(
    () => (barbers.data ?? []).filter((b) => b.active && b.branchId === appt.branchId && b.serviceIds.includes(appt.serviceId) && (can('agenda.read_all') || b.id === appt.barberId)),
    [barbers.data, appt, can],
  );
  const names = useMemo(() => new Map((barbers.data ?? []).map((b) => [b.id, b.displayName])), [barbers.data]);
  const avail = useAvailability({ serviceId: appt.serviceId, branchId: appt.branchId, barberId, date, excludeAppointmentId: appt.id });

  const save = useMutation({
    mutationFn: () => api.post<AppointmentDetail>(`/appointments/${appt.id}/reschedule`, { startsAt: picked!.startsAt, barberId: picked!.barberId, reason: reason.trim() || undefined }),
    onSuccess: (a) => {
      toast.success('Agendamento reagendado');
      const note = emailNote(a.emailStatus);
      if (note) toast.message(note);
      invalidate();
      onOpenChange(false);
    },
    onError: (e) => {
      toast.error(e instanceof HttpError && e.status === 409 ? 'Esse horário acabou de ser ocupado. Escolha outro.' : errorMessage(e));
      avail.refetch();
      setPicked(null);
    },
  });

  return (
    <FormModal
      open={open}
      onOpenChange={onOpenChange}
      title="Reagendar"
      description={`${appt.customerName} · ${appt.serviceName}`}
      footer={
        <>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Voltar
          </Button>
          <Button disabled={!picked || save.isPending} onClick={() => save.mutate()}>
            {save.isPending ? 'Salvando…' : 'Salvar novo horário'}
          </Button>
        </>
      }
    >
      <div className="grid gap-4">
        <div className="grid gap-4 sm:grid-cols-2">
          <Field id="rs-barber" label="Profissional">
            <Select value={barberId} onValueChange={(v) => (setBarberId(v), setPicked(null))}>
              <SelectTrigger id="rs-barber" className="w-full">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {options.map((b) => (
                  <SelectItem key={b.id} value={b.id}>
                    {b.displayName}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          <Field id="rs-date" label="Nova data">
            <Input id="rs-date" type="date" value={date} onChange={(e) => (setDate(e.target.value), setPicked(null))} />
          </Field>
        </div>
        <SlotPicker slots={avail.data} loading={avail.isLoading} error={avail.error} onRetry={() => avail.refetch()} selected={picked} onSelect={setPicked} tz={tz} barberNames={names} showBarber={false} />
        <Field id="rs-reason" label="Motivo (opcional)">
          <Textarea id="rs-reason" rows={2} value={reason} onChange={(e) => setReason(e.target.value)} />
        </Field>
      </div>
    </FormModal>
  );
}
